import PropTypes from 'prop-types';
import { useState, useMemo, useCallback } from 'react';
import { Formik } from 'formik';
import { useSnackbar } from 'notistack';

/* UI Elements */
import {
  Box,
  Typography,
  Button,
  Select,
  FormControl,
  MenuItem,
  TextField,
  FormHelperText,
  CircularProgress,
} from '@mui/material';
import { FormControlLabel, Checkbox, Radio, RadioGroup } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import ExperienceWalletSkillsAutocomplete from './ExperienceWalletSkillsAutocomplete';

/** Convert "MM/YYYY" or ISO "YYYY-MM-DD" string → Date object for DatePicker */
export function parseMMYYYY(str) {
  if (!str || typeof str !== 'string') return null;
  // Handle ISO format (YYYY-MM-DD). Parse to local time, not UTC, so the
  // DatePicker shows the same month the backend stored.
  if (/^\d{4}-\d{2}/.test(str)) {
    const [yyyy, mm] = str.split('-').map(Number);
    if (!yyyy || !mm) return null;
    return new Date(yyyy, mm - 1, 1);
  }
  const [mm, yyyy] = str.split('/');
  if (!mm || !yyyy || yyyy.length < 4) return null;
  return new Date(Number(yyyy), Number(mm) - 1, 1);
}

/** Convert Date object → "MM/YYYY" string for Formik */
export function formatMMYYYY(date) {
  if (!date || isNaN(date.getTime())) return '';
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = date.getFullYear();
  return `${mm}/${yyyy}`;
}

/** Convert date string to ISO format (YYYY-MM-DD) for the backend API */
export function toISODate(dateStr) {
  if (!dateStr) return null;
  // Already in ISO format (YYYY-MM-DD)
  if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) return dateStr.slice(0, 10);
  // MM/YYYY format → YYYY-MM-01
  const parts = dateStr.split('/');
  if (parts.length === 2) return `${parts[1]}-${parts[0].padStart(2, '0')}-01`;
  return dateStr;
}
import ExperienceWalletFieldsAutocomplete from './ExperienceWalletFieldsAutocomplete';
import MilitaryMOCAutocomplete from './MilitaryMOCAutocomplete';

/* i18n */
import { useIntl } from 'react-intl';
import { typeDict, lcwTypeLabels } from './ExperienceWalletDrawer';
import { getEffectiveFormScope } from 'lib/experienceHelpers';

import { apiRequest } from 'lib/API';

/* Analytics */
import { trackEvent } from 'lib/analytics';
import { useQueryClient } from '@tanstack/react-query';
import { BRANCH_OPTIONS, RANKS_BY_BRANCH } from 'constants/militaryOptions';
import { WORK_ITEMS, CREDENTIAL_TYPE_ITEMS } from 'constants/experienceTypeOptions';
import { EDUCATION_LEVEL_ITEMS, MILITARY_COURSE_ITEM, EDUCATION_LEVEL_BY_ID } from 'constants/educationLevelOptions';

// LER/DCC-imported credentials can carry raw external type codes that aren't valid
// dropdown options (no matching MenuItem key), so the "Select an educational experience
// or credential" dropdown can't highlight any option for them. Map each known raw code
// to the closest matching existing credential type so the field pre-selects correctly.
const RAW_CREDENTIAL_TYPE_ALIASES = {
  openbadge: 'badge',
  OpenBadgeCredential: 'badge',
  achievement: 'badge',
  AchievementCredential: 'badge',
};

// LER job_experience records of type 'degree' with no level (no specific degree|<id> to
// match) fall back to this existing education level option.
const FALLBACK_DEGREE_LEVEL_ID = 5; // "Some College-No Credential"

export function TypeSelect({
  onChange = () => {},
  onChangeCapture = () => {},
  value = '',
  disabled = false,
  name = 'type',
  onboarding = false,
  inputId = 'type-select-input',
  formScope,
  showMilitaryCourse = false,
}) {
  const intl = useIntl();

  // Add Education uses the education levels (EDUCATION_LEVEL_ITEMS) so the
  // "Select an educational experience" dropdown matches the high-level degree list.
  // Onboarding keeps the combined type list.
  // Military Course is appended only when the user has indicated military service.
  const educationItems = showMilitaryCourse
    ? [MILITARY_COURSE_ITEM, ...CREDENTIAL_TYPE_ITEMS, ...EDUCATION_LEVEL_ITEMS]
    : [...CREDENTIAL_TYPE_ITEMS, ...EDUCATION_LEVEL_ITEMS];
  const allItems = onboarding
    ? [...WORK_ITEMS, ...CREDENTIAL_TYPE_ITEMS, ...EDUCATION_LEVEL_ITEMS]
    : formScope === 'work'
    ? WORK_ITEMS
    : educationItems;

  return (
    <Select
      onChangeCapture={onChangeCapture}
      displayEmpty
      disabled={disabled}
      name={name}
      onChange={onChange}
      value={value}
      renderValue={(selected) => {
        if (!selected || !selected.length) {
          return formScope === 'education' ? intl.formatMessage({ id: 'experience.form.experiencePlaceholder' }) : '';
        }
        const matched = allItems.find((item) => item.key === selected);
        if (matched) return intl.formatMessage({ id: matched.labelId });
        if (typeDict[selected]) return intl.formatMessage({ id: typeDict[selected] });
        // LCW/DCC-sourced credentials (e.g. "openbadge") carry raw external type
        // strings that don't map to an i18n key; show the shared plain-text label.
        return lcwTypeLabels[selected] ?? selected;
      }}
      MenuProps={{
        sx: {
          maxHeight: 440,
          '& .MuiPaper-root': {
            borderRight: '1px solid #ccc',
            borderBottom: '1px solid #ccc',
            borderLeft: '1px solid #ccc',
          },
        },
      }}
      sx={{
        '.MuiFilledInput-input': {
          borderRadius: '4px 4px 0 0 !important',
          maxHeight: 43,
          boxSizing: 'border-box',
          padding: '15px 11px 13px!important',
        },
        '.MuiFilledInput-input:not(.Mui-disabled):not(:hover)': { backgroundColor: 'white' },
      }}
      inputProps={{
        title: intl.formatMessage({ id: 'experience.form.experienceTypeLabel' }),
        id: inputId,
      }}
      variant="filled"
      id="goals-add-experience-dropdown"
    >
      {allItems.map(({ key, labelId }) => (
        <MenuItem key={key} value={key} id={`${key}-${onboarding ? 'onboard-' : ''}dropdown`}>
          {intl.formatMessage({ id: labelId })}
        </MenuItem>
      ))}
    </Select>
  );
}

TypeSelect.propTypes = {
  onChange: PropTypes.func,
  onChangeCapture: PropTypes.func,
  name: PropTypes.string,
  value: PropTypes.string,
  inputId: PropTypes.string,
  disabled: PropTypes.bool,
  onboarding: PropTypes.bool,
  formScope: PropTypes.oneOf(['education', 'work', 'military']),
  showMilitaryCourse: PropTypes.bool,
};

export default function ExperienceWalletForm({
  experience: { type, field, title, skills, start_date, end_date, employer_provider, id, ...rest },
  experiences,
  setCurrentContent,
  currentIndex,
  replaceItem,
  appendItem,
  onSubmit,
  formScope,
  formId,
  onCategoryChange,
  onSubmittingChange,
  hasSelectedCategory = false,
  isResumeBuilder = false,
  showMilitaryCourse = false,
  saveMilitaryCourses,
  saveAcademicDegrees,
}) {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  // apprenticeship/internship are work experience types (see WORK_ITEMS);
  // apperenticeshipRTI is the distinct education/credential counterpart.
  const derivedFormScope = replaceItem
    ? formScope === 'military' || type === 'militaryExperience'
      ? 'military'
      : WORK_ITEMS.some(({ key }) => key === type)
      ? 'work'
      : 'education'
    : formScope;

  const [skillsOpen, setSkillsOpen] = useState(false);
  const [fieldOpen, setFieldOpen] = useState(false);
  // Auto-check Present if: is_current explicitly true from cache/API only
  const [isCurrentJob, setIsCurrentJob] = useState(derivedFormScope !== 'education' && rest.is_current === true);
  const [isExperienceCurrent, setIsExperienceCurrent] = useState(
    derivedFormScope === 'education' && rest.is_current === true
  );
  const [fieldSuggestion, setFieldSuggestion] = useState('');
  const [gettingSkills, setGettingSkills] = useState(false);

  // Create a Map for O(1) branch API value lookup
  const branchBackendMap = useMemo(() => new Map(BRANCH_OPTIONS.map((b) => [b.value, b.backendName])), []);

  const RequiredLabel = ({ id }) => (
    <Typography sx={{ fontWeight: 700 }}>
      {intl.formatMessage({ id })}
      <Typography sx={{ color: (theme) => theme.palette.error.main }} component="span">
        *
      </Typography>
    </Typography>
  );
  RequiredLabel.propTypes = { id: PropTypes.string.isRequired };

  const initialValues = {
    type: RAW_CREDENTIAL_TYPE_ALIASES[type] || type,
    // LER 'degree' records don't carry a level, so there's no degree|<id> to match —
    // fall back to an existing education level option instead of leaving it unmatched.
    level: rest.level ?? (type === 'degree' ? FALLBACK_DEGREE_LEVEL_ID : null),
    field,
    title,
    skills,
    start_date,
    end_date,
    employer_provider,
    comments: rest.comments ?? '',
    branch: rest.branch ?? '',
    rank: rest.rank ?? '',
    experienceCategory: rest.experienceCategory ?? (formScope === 'military' ? 'military' : ''), // Auto set military when opened from Add Military Experience button
  };

  // Reusable function to fetch skills by SOC code
  const fetchSkillsBySoc = useCallback(
    (socCode, setFieldValue, { silent = false } = {}) => {
      setGettingSkills(true);
      setSkillsOpen(false);
      apiRequest('GET', '/users/me/skills/socs', {
        params: { socs: socCode.replace(/-/g, ''), upvote_skills: 10 },
      })
        .then((r) => {
          setFieldValue('skills', r?.skills.slice(0, 5));
          setGettingSkills(false);
        })
        .catch((error) => {
          console.error('Error fetching skills for SOC:', socCode, error);
          setFieldValue('skills', []);
          setGettingSkills(false);
          if (!silent) {
            enqueueSnackbar(intl.formatMessage({ id: 'experience.form.skillsLoadError' }), {
              variant: 'warning',
              preventDuplicate: true,
            });
          }
        });
    },
    [enqueueSnackbar, intl]
  );

  const placeholderDict = {
    apprenticeship: intl.formatMessage({ id: 'experience.form.apprenticeshipDescriptionPlaceholder' }),
    assessment: intl.formatMessage({ id: 'experience.form.assessmentDescriptionPlaceholder' }),
    badge: intl.formatMessage({ id: 'experience.form.badgeDescriptionPlaceholder' }),
    certificate: intl.formatMessage({ id: 'experience.form.certificateDescriptionPlaceholder' }),
    certification: intl.formatMessage({ id: 'experience.form.certificationDescriptionPlaceholder' }),
    course: intl.formatMessage({ id: 'experience.form.courseDescriptionPlaceholder' }),
    degree: intl.formatMessage({ id: 'experience.form.degreeDescriptionPlaceholder' }),
    license: intl.formatMessage({ id: 'experience.form.licenseDescriptionPlaceholder' }),
    internship: intl.formatMessage({ id: 'experience.form.internshipDescriptionPlaceholder' }),
    employment: intl.formatMessage({ id: 'experience.form.jobDescriptionPlaceholder' }),
    volunteer: intl.formatMessage({ id: 'experience.form.volunteerDescriptionPlaceholder' }),
    militaryCourse: intl.formatMessage({ id: 'experience.form.militaryCourseDescriptionPlaceholder' }),
    militaryExperience: intl.formatMessage({ id: 'experience.form.militaryCourseDescriptionPlaceholder' }),
    apperenticeshipRTI: intl.formatMessage({ id: 'experience.form.apprenticeshipDescriptionPlaceholder' }),
  };

  // Constants for validation - defined once outside validation function
  const MAX_COMMENTS = 500;
  const LETTERS_NUMBERS_REGEX = /^[a-zA-Z0-9\s.,'\-()\//&;@!?%\\*=]+$/;
  const CONTAINS_LETTER_REGEX = /[a-zA-Z]/;

  // Helper: validate MM/YYYY or ISO date is not future and year >= 1900
  // Defined outside Formik to avoid recreation on every render
  const validateMMYYYYDate = (dateStr) => {
    let month, year;
    if (/^\d{4}-\d{2}/.test(dateStr)) {
      // ISO format: YYYY-MM-DD or YYYY-MM
      [year, month] = dateStr.split('-').map(Number);
    } else {
      [month, year] = dateStr.split('/').map(Number);
    }
    if (!month || !year) return null;
    const now = new Date();
    if (year > now.getFullYear() || (year === now.getFullYear() && month > now.getMonth() + 1)) {
      return 'experience.form.futureDateNotAllowed';
    }
    if (year < 1900) return 'experience.form.invalidYear';
    return null;
  };

  // True when editing a wallet-imported experience; only the skills field should be editable.
  const isWalletEdit = Boolean(rest.wallet) && Boolean(replaceItem);

  return (
    <Formik
      initialValues={initialValues}
      validate={(values) => {
        // When editing a wallet experience, only skills are editable — skip all other validation
        if (isWalletEdit) return {};

        const errors = {};
        const isMilitaryCategory = formScope === 'military' && values.experienceCategory === 'military';
        const effectiveFormScope = getEffectiveFormScope(formScope, values.experienceCategory, derivedFormScope);

        // Type required for all forms (including military civilian category)
        if (!values.type && effectiveFormScope !== 'military') {
          errors.type = intl.formatMessage({ id: 'jobSearchActivity.form.fieldRequired' });
        }
        if (
          (isMilitaryCategory || effectiveFormScope === 'work') &&
          (!values.field ||
            (isMilitaryCategory ? !values.field?.moc_code && !values.field?.soc_code : !values.field?.soc)) &&
          !(isResumeBuilder && replaceItem)
        ) {
          errors.field = intl.formatMessage({ id: 'jobSearchActivity.form.fieldRequired' });
        }
        // Military forms never render a title field — the MOC autocomplete is the title proxy.
        if (!values.title && !isMilitaryCategory) {
          errors.title = intl.formatMessage({ id: 'jobSearchActivity.form.fieldRequired' });
        }
        if (values.title && !LETTERS_NUMBERS_REGEX.test(values.title)) {
          errors.title = intl.formatMessage({ id: 'profile.validation.basicPunctiation' });
        }
        if (values.title && !errors.title && !CONTAINS_LETTER_REGEX.test(values.title)) {
          errors.title = intl.formatMessage({ id: 'profile.validation.mustContainLetter' });
        }
        // Skills validation for all form types (work/military/education)
        if (
          (effectiveFormScope === 'work' || effectiveFormScope === 'education' || isMilitaryCategory) &&
          (!values.skills?.length || values.skills.length < 5)
        ) {
          errors.skills = intl.formatMessage({ id: 'experience.form.minimumSkillsRequired' });
        }
        if (!values.start_date) {
          errors.start_date = intl.formatMessage({ id: 'jobSearchActivity.form.fieldRequired' });
        }

        // Validate start_date is not in the future and year >= 1900
        if (values.start_date) {
          const startErr = validateMMYYYYDate(values.start_date);
          if (startErr) errors.start_date = intl.formatMessage({ id: startErr });
        }

        // Validate end_date is not in the future and year >= 1900
        if (values.end_date) {
          const endErr = validateMMYYYYDate(values.end_date);
          if (endErr) errors.end_date = intl.formatMessage({ id: endErr });
        }

        // Validate end date is after start date (consolidated for all form types)
        if (values.end_date && values.start_date) {
          const [startMonth, startYear] = values.start_date.split('/').map(Number);
          const [endMonth, endYear] = values.end_date.split('/').map(Number);
          const isEndBeforeStart = endYear < startYear || (endYear === startYear && endMonth < startMonth);

          if (isEndBeforeStart) {
            errors.end_date = intl.formatMessage({ id: 'experience.form.endDatePrior' });
          }
        }

        // Check if end date is required
        if (
          (effectiveFormScope === 'work' && !isCurrentJob && !values.end_date) ||
          (effectiveFormScope === 'education' && !isExperienceCurrent && !values.end_date)
        ) {
          errors.end_date = intl.formatMessage({ id: 'jobSearchActivity.form.fieldRequired' });
        }
        if (
          !isCurrentJob &&
          !isExperienceCurrent &&
          !values.end_date &&
          effectiveFormScope !== 'work' &&
          effectiveFormScope !== 'education'
        ) {
          errors.end_date = intl.formatMessage({ id: 'jobSearchActivity.form.fieldRequired' });
        }
        if (!values.employer_provider && !isMilitaryCategory) {
          errors.employer_provider = intl.formatMessage({ id: 'jobSearchActivity.form.fieldRequired' });
        }
        if (values.employer_provider && !LETTERS_NUMBERS_REGEX.test(values.employer_provider)) {
          errors.employer_provider = intl.formatMessage({ id: 'profile.validation.basicPunctiation' });
        }
        if (
          values.employer_provider &&
          !errors.employer_provider &&
          !CONTAINS_LETTER_REGEX.test(values.employer_provider)
        ) {
          errors.employer_provider = intl.formatMessage({ id: 'profile.validation.mustContainLetter' });
        }
        if (isMilitaryCategory) {
          if (!values.branch) {
            errors.branch = intl.formatMessage({ id: 'jobSearchActivity.form.fieldRequired' });
          }
          if (!values.rank) {
            errors.rank = intl.formatMessage({ id: 'jobSearchActivity.form.fieldRequired' });
          }
        }
        if (values.comments && values.comments.length > MAX_COMMENTS) {
          errors.comments = intl.formatMessage({ id: 'experience.form.commentsMax' }, { n: MAX_COMMENTS });
        }
        if (values.comments && !LETTERS_NUMBERS_REGEX.test(values.comments)) {
          errors.comments = intl.formatMessage({ id: 'profile.validation.basicPunctiation' });
        }
        return errors;
      }}
      onSubmit={(values, { setSubmitting }) => {
        // Clear any previous field suggestion errors on new submit
        setFieldSuggestion('');
        // Check if this is military category
        const isMilitaryCategory = formScope === 'military' && values.experienceCategory === 'military';

        if (onSubmit) {
          onSubmit({
            ...values,
            end_date: isExperienceCurrent || isCurrentJob ? null : values.end_date,
            is_current: isExperienceCurrent || isCurrentJob,
          });
          return;
        }

        // Military courses and academic degrees each persist into their own jst
        // section rather than education[]/job_experience. Only the save call
        // differs; the surrounding submit/close plumbing is identical.
        const persistToJst = (save, errorLabel) => {
          setSubmitting(true);
          if (onSubmittingChange) onSubmittingChange(true);
          trackEvent('EXPERIENCE_SAVE');
          save()
            .then(() => {
              if (appendItem) {
                appendItem();
                trackEvent('EXPERIENCE_MANUAL_ADD');
              } else if (replaceItem) {
                trackEvent('EXPERIENCE_MANUAL_EDIT');
              }
              setSubmitting(false);
              if (onSubmittingChange) onSubmittingChange(false);
              if (setCurrentContent) setCurrentContent('display');
            })
            .catch((error) => {
              setSubmitting(false);
              if (onSubmittingChange) onSubmittingChange(false);
              console.error(errorLabel, error);
              if (error?.response?.data?.detail) {
                console.error('Backend validation error:', error.response.data.detail);
              }
            });
        };

        // Military courses persist to jst.military_courses only — never education[].
        // Merge the editable fields onto the full stored object so JST-only fields
        // (ace_identifier, credit_area, description, location…) survive.
        const isMilitaryCourse = values.type === 'militaryCourse' || rest.isMilitaryCourse === true;
        if (isMilitaryCourse && saveMilitaryCourses) {
          const enrolled = isExperienceCurrent || isCurrentJob;
          const mergedCourse = {
            ...(rest._raw || {}),
            course_title: values.title,
            institution_provider: values.employer_provider,
            start_date: toISODate(values.start_date),
            end_date: enrolled ? null : toISODate(values.end_date),
            currently_enrolled: enrolled,
            parsed_skills: (values.skills || []).map(({ id: skillId, name, type: skillType }) => ({
              id: skillId,
              name,
              type: skillType || 'skill',
            })),
          };
          persistToJst(
            () =>
              saveMilitaryCourses(mergedCourse, {
                courseId: rest.military_course_id ?? null,
                index: typeof rest.militaryCourseIndex === 'number' ? rest.militaryCourseIndex : -1,
              }),
            'Error saving military course:'
          );
          return;
        }

        // Academic degrees/certificates added on the JST stay in
        // jst.academic_degrees_certificates and are edited in place from here.
        if (rest.isAcademicDegree === true && saveAcademicDegrees) {
          const enrolled = isExperienceCurrent || isCurrentJob;
          // Education levels are picked as `degree` + a level id; every other
          // credential is its own type. Both store the credential enum.
          const credential =
            values.type === 'degree' ? EDUCATION_LEVEL_BY_ID.get(Number(values.level))?.value || '' : values.type || '';
          const mergedDegree = {
            ...(rest._raw || {}),
            education_experience_credential: credential,
            institution_provider: values.employer_provider,
            name: values.title,
            start_date: toISODate(values.start_date),
            end_date: enrolled ? null : toISODate(values.end_date),
            currently_enrolled: enrolled,
            skills_gained: (values.skills || []).map(({ id: skillId, name, type: skillType }) => ({
              id: skillId,
              name,
              type: skillType || 'skill',
            })),
          };
          persistToJst(
            () =>
              saveAcademicDegrees(mergedDegree, {
                index: typeof rest.academicDegreeIndex === 'number' ? rest.academicDegreeIndex : -1,
              }),
            'Error saving academic degree:'
          );
          return;
        }

        if (appendItem || replaceItem) {
          // Helper to normalize experience fields property and ensure required fields exist
          const normalizeExperience = (exp) => {
            const { field, fields, ...rest } = exp;

            // Normalize fields to array of objects format
            const normalizedFields = (() => {
              // Already in correct format (array of objects)
              if (Array.isArray(fields) && fields.length > 0 && typeof fields[0] === 'object') {
                return fields;
              }
              // String array format - convert first element
              if (Array.isArray(fields) && fields.length > 0) {
                return [{ soc: fields[0], title: field?.title || '' }];
              }
              // Single string format - convert to array
              if (fields) {
                return [{ soc: fields, title: field?.title || '' }];
              }
              // Field object format - convert to array
              if (field?.soc) {
                return [{ soc: field.soc, title: field.title || '' }];
              }
              return [];
            })();

            return {
              ...rest,
              ...(normalizedFields.length > 0 && { fields: normalizedFields }),
              skills: exp.skills || [], // Ensure skills is always an array
              title: exp.title || '', // Ensure title exists
              employer_provider: exp.employer_provider || '', // Ensure employer_provider exists
            };
          };

          let newExperiences = experiences.map(normalizeExperience);

          // For wallet edits, ignore all form values except skills — use original prop values
          // so that DOM manipulation cannot alter immutable wallet credential fields.
          const itemToAddOrUpdate = isWalletEdit
            ? {
                sourceType: isMilitaryCategory ? 'military' : formScope === 'education' ? 'education' : 'work',
                title: isMilitaryCategory && field?.title ? field.title : title,
                ...(!isMilitaryCategory ? { employer_provider } : {}),
                skills: values.skills || [],
                start_date,
                ...(!(isExperienceCurrent || isCurrentJob) ? { end_date } : {}),
                is_current: isExperienceCurrent || isCurrentJob,
                type: type || (isMilitaryCategory ? 'employment' : ''),
                level: rest.level ?? null,
                comments: rest.comments?.trim() ? rest.comments.trim() : null,
                ...(field?.soc ? { fields: [{ soc: field.soc, title: field.title || '' }] } : {}),
                ...(rest.branch ? { branch_of_service: rest.branch } : {}),
                ...(rest.rank ? { rank: rest.rank } : {}),
                ...(rest.origin ? { origin: rest.origin } : {}),
                ...(rest.ctid ? { ctid: rest.ctid } : {}),
              }
            : {
                sourceType: isMilitaryCategory ? 'military' : formScope === 'education' ? 'education' : 'work',
                title: isMilitaryCategory && values.field?.moc_title ? values.field.moc_title : values.title,
                ...(!isMilitaryCategory ? { employer_provider: values.employer_provider } : {}),
                skills: values.skills || [],
                start_date: values.start_date,
                ...(!(isCurrentJob || isExperienceCurrent)
                  ? {
                      end_date: values.end_date,
                    }
                  : {}),
                is_current: isExperienceCurrent || isCurrentJob,
                type: values.type || (isMilitaryCategory ? 'employment' : ''),
                level: values.type === 'degree' ? values.level ?? null : null,
                comments: values.comments?.trim() ? values.comments.trim() : null,
                ...(values?.field?.soc ? { fields: [{ soc: values.field.soc, title: values.field.title || '' }] } : {}),
                ...(values.branch ? { branch_of_service: values.branch } : {}),
                ...(values.rank ? { rank: values.rank } : {}),
                ...(isMilitaryCategory && values.field?.moc_code ? { moc_code: values.field.moc_code } : {}),
                ...(isMilitaryCategory && values.field?.moc_title ? { moc_title: values.field.moc_title } : {}),
                ...(isMilitaryCategory && values.field?.soc_title ? { soc_title: values.field.soc_title } : {}),
                // occupation_id is the selected civilian SOC code; fall back to the
                // previously saved value when editing without re-picking a job title.
                ...(isMilitaryCategory && (values.field?.soc_code || rest.occupation_id)
                  ? { occupation_id: values.field?.soc_code || rest.occupation_id }
                  : {}),
                ...(isMilitaryCategory && rest.soc_id ? { soc_id: rest.soc_id } : {}),
              };
          if (appendItem) {
            newExperiences = [...newExperiences, itemToAddOrUpdate];
          } else if (replaceItem) {
            newExperiences[currentIndex] = itemToAddOrUpdate;
          }
          setSubmitting(true);
          if (onSubmittingChange) onSubmittingChange(true);
          trackEvent('EXPERIENCE_SAVE');

          // Build separate arrays for each backend schema section
          const jobExperiences = [];
          const militaryExperiences = [];

          newExperiences.forEach((exp) => {
            const { sourceType, branch, ...rest } = exp;

            if (sourceType === 'military' || rest.branch_of_service || branch) {
              // Map to jst.military_experience[] schema
              const rawBranch = branch || rest.branch_of_service || '';
              const milBranch = branchBackendMap.get(rawBranch) || rawBranch;
              militaryExperiences.push({
                ...(rest.soc_id ? { id: rest.soc_id } : {}),
                occupation_id: rest.occupation_id || '',
                experience_title: rest.moc_code && rest.title ? `${rest.title} (${rest.moc_code})` : rest.title || '',
                moc_code: rest.moc_code || '',
                moc_title: rest.moc_title || '',
                soc: rest.soc_title || '',
                branch_of_service: milBranch,
                rank: rest.rank || '',
                start_date: toISODate(rest.start_date),
                end_date: rest.is_current ? null : toISODate(rest.end_date),
                currently_enrolled: rest.is_current ? true : null,
                description: rest.comments || rest.description || '',
                parsed_skills: (rest.skills || []).map(({ type, name, id: skillId, rating }) => ({
                  type: type || 'skill',
                  name,
                  id: skillId,
                  ...(rating != null ? { rating } : {}),
                })),
              });
            } else {
              // Map to job_experience[] schema.
              // Prod schema: description = focus/role/major text, comments = tell-me-about, title = null.
              jobExperiences.push({
                title: null,
                employer_provider: rest.employer_provider || '',
                description: rest.title || null,
                comments: rest.comments?.trim() ? rest.comments.trim() : null,
                start_date: toISODate(rest.start_date),
                end_date: toISODate(rest.end_date),
                is_current: rest.is_current ?? null,
                type: rest.type || '',
                ...(rest.fields ? { fields: rest.fields } : {}),
                ...(rest.type === 'degree' && rest.level != null ? { level: rest.level } : {}),
                ...(rest.origin ? { origin: rest.origin } : {}),
                ...(rest.ctid ? { ctid: rest.ctid } : {}),
                skills: (rest.skills || []).map(({ type, name, id: skillId }) => ({
                  type: type || 'skill',
                  name,
                  id: skillId,
                })),
              });
            }
          });

          // Build the payload matching the PUT API schema.
          // Spread existing jst so other JST sections (military_courses, other_learning, etc.)
          // are preserved when only military_experience is being edited here.
          const existingData = queryClient.getQueryData(['/users/me/experience/']);
          const existingJst = existingData?.jst || {};

          // When experiences is empty (onboarding), merge with the cache so prior API entries
          // are not lost. When experiences is non-empty (ExperienceWallet), the prop already
          // contains all existing items, so merging with the cache would duplicate them.
          const useCache = appendItem && experiences.length === 0;
          // Only pull prior entries from the cache for the type NOT being saved right now.
          // If we merge prior entries of the same type, going Back then Next again produces
          // a duplicate because the first save already wrote that entry to the cache.
          const priorJobExp = useCache && jobExperiences.length === 0 ? existingData?.job_experience || [] : [];
          const priorMilExp =
            useCache && militaryExperiences.length === 0 ? existingData?.jst?.military_experience || [] : [];

          const payload = {
            job_experience: [...priorJobExp, ...jobExperiences],
            jst: {
              ...existingJst,
              military_experience: [...priorMilExp, ...militaryExperiences],
            },
          };

          apiRequest('PUT', '/users/me/experience/', { data: payload })
            .then(() => {
              const newField = values.field ? { soc: values.field.soc, title: values.field.title } : null;
              if (appendItem) {
                appendItem({
                  ...itemToAddOrUpdate,
                  ...(newField ? { field: newField } : {}),
                });
                trackEvent('EXPERIENCE_MANUAL_ADD');
              }
              if (replaceItem) {
                if (!itemToAddOrUpdate.wallet) {
                  trackEvent('EXPERIENCE_MANUAL_EDIT', itemToAddOrUpdate);
                }
                replaceItem(currentIndex, {
                  id,
                  ...itemToAddOrUpdate,
                  ...(newField ? { field: newField } : {}),
                });
              }
              if (values.type) {
                trackEvent('EXPERIENCE_TYPE', values.type);
              }
              setSubmitting(false);
              if (onSubmittingChange) onSubmittingChange(false);
              // Only call setCurrentContent if it exists (ExperienceWallet component)
              if (setCurrentContent) {
                setCurrentContent('display');
              }
              // Update cache immediately so back navigation can pre-populate.
              // Use the full payload arrays (which include preserved prior entries of
              // the other type) rather than just the newly-added entries — otherwise
              // saving a military entry would wipe civilian entries from the cache, or
              // vice versa.
              queryClient.setQueryData(['/users/me/experience/'], (old) => ({
                ...old,
                job_experience: payload.job_experience,
                jst: {
                  ...(old?.jst || {}),
                  military_experience: payload.jst.military_experience,
                },
              }));
              return;
            })
            .catch((error) => {
              setSubmitting(false);
              if (onSubmittingChange) onSubmittingChange(false);
              console.error('Error saving experience:', error);
              // Check if it's a 400 or 422 error
              if (
                error?.response?.status === 400 ||
                error?.response?.status === 422 ||
                error?.message?.includes('400') ||
                error?.message?.includes('422')
              ) {
                setFieldSuggestion(intl.formatMessage({ id: 'jobSearchActivity.form.fieldRequired' }));
              }
              // Show error message to user
              if (error?.response?.data?.detail) {
                console.error('Backend validation error:', error.response.data.detail);
              }
            });
        }
      }}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        setFieldValue,
        submitCount,
      }) => {
        const currentRanks = values.branch ? RANKS_BY_BRANCH[values.branch] || [] : [];
        // Calculate effective form scope: when formScope is 'military' and civilian is selected, treat as 'work'
        const effectiveFormScope = getEffectiveFormScope(formScope, values.experienceCategory, derivedFormScope);
        const isMilitaryForm = formScope === 'military' && values.experienceCategory === 'military';

        return (
          <form onSubmit={handleSubmit} id={formId}>
            {setCurrentContent && (
              <Box
                sx={{
                  py: 1,
                  mb: 1,
                  mt: 1,
                }}
              >
                <Typography sx={{ fontWeight: 700, pr: 1, textAlign: isResumeBuilder ? 'center' : 'left' }}>
                  {intl.formatMessage({
                    id:
                      formScope === 'military' && !values.experienceCategory
                        ? 'experience.form.selectExperienceType'
                        : isMilitaryForm
                        ? replaceItem
                          ? 'experience.form.editMilitaryExperience'
                          : 'experience.form.addMilitaryExperience'
                        : replaceItem
                        ? effectiveFormScope === 'work'
                          ? 'experience.form.editWorkExperience'
                          : 'experience.form.editEducationExperience'
                        : effectiveFormScope === 'work'
                        ? 'experience.form.addWorkExperience'
                        : 'experience.form.addEducationExperience',
                  })}
                </Typography>
              </Box>
            )}

            {/* Experience category selection for military formScope - show when parent supports category change */}
            {formScope === 'military' && (isResumeBuilder || onCategoryChange) && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  pt: 0,
                  pb: 1,
                }}
              >
                <Typography sx={{ fontWeight: 700, mb: 1 }}>
                  {intl.formatMessage({ id: 'experience.form.workExperienceLabel' })}
                  <Typography sx={{ color: (theme) => theme.palette.error.main }} component="span">
                    *
                  </Typography>
                </Typography>
                <RadioGroup
                  row
                  sx={{ justifyContent: 'center', gap: 0.5 }}
                  value={values.experienceCategory}
                  onChange={(e) => {
                    const newCategory = e.target.value;
                    setFieldValue('experienceCategory', newCategory);
                    // Notify parent component of category change
                    if (onCategoryChange) {
                      onCategoryChange(newCategory);
                    }
                    // Reset relevant fields when switching categories
                    if (e.target.value === 'civilian') {
                      setFieldValue('branch', '');
                      setFieldValue('rank', '');
                      setFieldValue('field', null);
                      setFieldValue('skills', []);
                      setFieldValue('title', '');
                    } else {
                      setFieldValue('type', '');
                      setFieldValue('employer_provider', '');
                      setFieldValue('skills', []);
                      setFieldValue('title', '');
                      setFieldValue('field', null);
                    }
                  }}
                >
                  <FormControlLabel
                    value="civilian"
                    control={<Radio />}
                    label={intl.formatMessage({ id: 'experience.form.civilianExperience' })}
                  />
                  <FormControlLabel
                    value="military"
                    control={<Radio />}
                    label={intl.formatMessage({ id: 'experience.form.militaryExperience' })}
                  />
                </RadioGroup>
              </Box>
            )}

            {/* Hide type selection for military category */}
            {(formScope !== 'military' || (hasSelectedCategory && values.experienceCategory === 'civilian')) && (
              <Box sx={{ display: 'flex', mb: 1, flexDirection: 'column' }}>
                <Typography sx={{ fontWeight: 700 }}>
                  {intl.formatMessage({
                    id:
                      effectiveFormScope === 'work'
                        ? 'experience.form.workExperienceTypeLabel'
                        : 'experience.form.educationExperienceTypeLabel',
                  })}
                  <Typography sx={{ color: (theme) => theme.palette.error.main }} component="span">
                    *
                  </Typography>
                </Typography>
                <FormControl variant="filled" error={touched.type && Boolean(errors.type)}>
                  <TypeSelect
                    onChange={(e) => {
                      setIsCurrentJob(false);
                      setIsExperienceCurrent(false);
                      const v = e.target.value;
                      if (typeof v === 'string' && v.startsWith('degree|')) {
                        const lvl = v.split('|')[1] || null;
                        setFieldValue('type', 'degree');
                        setFieldValue('level', lvl);
                      } else {
                        setFieldValue('type', v);
                        setFieldValue('level', null);
                      }
                    }}
                    value={values.type === 'degree' && values.level ? `degree|${values.level}` : values.type}
                    disabled={isWalletEdit}
                    formScope={effectiveFormScope}
                    showMilitaryCourse={showMilitaryCourse}
                  />
                  {touched.type && errors.type ? <FormHelperText>{errors.type}</FormHelperText> : null}
                </FormControl>
              </Box>
            )}

            {/* Form fields - only show after category selection for military formScope */}
            {(formScope !== 'military' || hasSelectedCategory || values.experienceCategory) && (
              <>
                {/* Military-specific fields */}
                {isMilitaryForm ? (
                  <>
                    <Box sx={{ display: 'flex', mb: 1, flexDirection: 'column' }}>
                      <RequiredLabel id="experience.form.branchOfService" />
                      <FormControl variant="filled" error={touched.branch && Boolean(errors.branch)}>
                        <Select
                          name="branch"
                          value={values.branch || ''}
                          onChange={(e) => {
                            handleChange(e);
                            setFieldValue('rank', '');
                          }}
                          onBlur={handleBlur}
                          displayEmpty
                        >
                          <MenuItem value="" disabled>
                            {intl.formatMessage({ id: 'experience.form.selectBranch' })}
                          </MenuItem>
                          {BRANCH_OPTIONS.map((branch) => (
                            <MenuItem key={branch.value} value={branch.value}>
                              {intl.formatMessage({ id: branch.labelId })}
                            </MenuItem>
                          ))}
                        </Select>
                        {touched.branch && errors.branch && <FormHelperText>{errors.branch}</FormHelperText>}
                      </FormControl>
                    </Box>

                    <Box sx={{ display: 'flex', mb: 1, flexDirection: 'column' }}>
                      <RequiredLabel id="experience.form.rank" />
                      <FormControl variant="filled" error={touched.rank && Boolean(errors.rank)}>
                        <Select
                          name="rank"
                          value={values.rank || ''}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          displayEmpty
                          disabled={!values.branch}
                        >
                          <MenuItem value="" disabled>
                            {intl.formatMessage({
                              id: values.branch ? 'experience.form.selectRank' : 'experience.form.selectBranchFirst',
                            })}
                          </MenuItem>
                          {currentRanks.map((rank) => (
                            <MenuItem key={rank.value} value={rank.value}>
                              {rank.label}
                            </MenuItem>
                          ))}
                        </Select>
                        {touched.rank && errors.rank && <FormHelperText>{errors.rank}</FormHelperText>}
                      </FormControl>
                    </Box>

                    <Box sx={{ display: 'flex', mb: 1, flexDirection: 'column' }}>
                      <RequiredLabel id="experience.form.militaryJobTitle" />
                      <MilitaryMOCAutocomplete
                        id="military-job-title-dropdown"
                        branch={values.branch}
                        value={values.field}
                        disabled={isWalletEdit}
                        onChange={(newValue) => {
                          setFieldSuggestion('');
                          setFieldValue('field', newValue);
                        }}
                        onSkillsLoadStart={() => setGettingSkills(true)}
                        onSkillsLoaded={(skills) => {
                          setFieldValue('skills', skills);
                          setGettingSkills(false);
                        }}
                        helperText={fieldSuggestion || ((touched.field || submitCount > 0) && errors.field) || ''}
                        error={
                          ((touched.field || submitCount > 0) && Boolean(errors.field)) || Boolean(fieldSuggestion)
                        }
                      />
                    </Box>
                  </>
                ) : (
                  <>
                    <Box sx={{ display: 'flex', mb: 1, flexDirection: 'column' }}>
                      <Typography sx={{ fontWeight: 700 }}>
                        {intl.formatMessage({
                          id:
                            effectiveFormScope === 'work'
                              ? 'experience.form.workProvider'
                              : 'experience.form.educationProvider',
                        })}
                        <Typography sx={{ color: (theme) => theme.palette.error.main }} component="span">
                          *
                        </Typography>
                      </Typography>
                      <Box>
                        <TextField
                          sx={{
                            '.MuiFilledInput-input': { borderRadius: '4px 4px 0 0 !important' },
                            '.MuiFilledInput-input:not(.Mui-disabled):not(:hover)': { backgroundColor: 'white' },
                          }}
                          error={touched.employer_provider && Boolean(errors.employer_provider)}
                          fullWidth
                          disabled={isWalletEdit}
                          helperText={touched.employer_provider && errors.employer_provider}
                          placeholder={
                            effectiveFormScope === 'work'
                              ? ''
                              : intl.formatMessage({ id: 'experience.form.educationProviderPlaceholder' })
                          }
                          margin="normal"
                          name="employer_provider"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          type="text"
                          variant="filled"
                          inputProps={{
                            sx: {
                              '&::placeholder': {
                                opacity: 1,
                              },
                            },
                            title: intl.formatMessage({
                              id:
                                effectiveFormScope === 'work'
                                  ? 'experience.form.workProvider'
                                  : 'experience.form.educationProvider',
                            }),
                          }}
                          value={values.employer_provider ?? ''}
                          id="employer-provider-add-experience-dropdown"
                        />
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', mb: 1, flexDirection: 'column' }}>
                      <Typography sx={{ fontWeight: 700 }}>
                        {intl.formatMessage({
                          id:
                            effectiveFormScope === 'work'
                              ? 'experience.form.workTitle'
                              : 'experience.form.educationTitle',
                        })}
                        <Typography sx={{ color: (theme) => theme.palette.error.main }} component="span">
                          *
                        </Typography>
                      </Typography>
                      <Box>
                        <TextField
                          sx={{
                            '.MuiFilledInput-input': { borderRadius: '4px 4px 0 0 !important' },
                            '.MuiFilledInput-input:not(.Mui-disabled):not(:hover)': { backgroundColor: 'white' },
                          }}
                          error={touched.title && Boolean(errors.title)}
                          fullWidth
                          helperText={touched.title && errors.title}
                          margin="normal"
                          name="title"
                          disabled={isWalletEdit}
                          onBlur={handleBlur}
                          onChange={(e) => {
                            handleChange(e);
                            // Reset occupation when title changes
                            if (values.field) {
                              setFieldValue('field', null);
                            }
                          }}
                          type="text"
                          placeholder={values.type ? placeholderDict[values.type] : ''}
                          variant="filled"
                          value={values.title}
                          inputProps={{
                            sx: {
                              '&::placeholder': {
                                opacity: 1,
                              },
                            },
                            title: intl.formatMessage({
                              id:
                                effectiveFormScope === 'work'
                                  ? 'experience.form.workTitle'
                                  : 'experience.form.educationTitle',
                            }),
                          }}
                          id="experience-add-experience-dropdown"
                        />
                      </Box>
                    </Box>
                    {effectiveFormScope === 'work' && (
                      <Box>
                        <Typography sx={{ fontWeight: 700 }}>
                          {intl.formatMessage({
                            id:
                              effectiveFormScope === 'work'
                                ? 'experience.form.workFieldsLabel'
                                : 'experience.form.educationFieldsLabel',
                          })}
                          <Typography sx={{ color: (theme) => theme.palette.error.main }} component="span">
                            *
                          </Typography>
                        </Typography>
                        <ExperienceWalletFieldsAutocomplete
                          id="experience-select-field-dropdown"
                          isDisabled={isWalletEdit || gettingSkills}
                          value={values.field}
                          initialInputValue={values.title || ''}
                          onChange={(_, newValue) => {
                            setFieldSuggestion('');
                            setFieldValue('field', newValue);
                            if (newValue) {
                              fetchSkillsBySoc(newValue.soc, setFieldValue);
                            }
                          }}
                          onAutoMatch={(match) => {
                            setFieldValue('field', match);
                            setFieldOpen(false);
                            fetchSkillsBySoc(match.soc, setFieldValue, { silent: true });
                          }}
                          helperText={
                            fieldSuggestion || ((touched.field || submitCount > 0) && errors.field ? errors.field : '')
                          }
                          error={
                            ((touched.field || submitCount > 0) && Boolean(errors.field)) || Boolean(fieldSuggestion)
                          }
                          open={fieldOpen}
                          setOpen={setFieldOpen}
                        />
                      </Box>
                    )}
                  </>
                )}

                {/* Skills section - common for all types */}
                <Box>
                  <Typography sx={{ fontWeight: 700 }}>
                    {intl.formatMessage({
                      id:
                        effectiveFormScope === 'work'
                          ? 'experience.form.selectASkill'
                          : 'experience.form.selectASkillEducation',
                    })}
                    {isMilitaryForm ? ':' : ''}
                    <Typography sx={{ color: (theme) => theme.palette.error.main }} component="span">
                      *
                    </Typography>
                  </Typography>
                  {gettingSkills ? (
                    <Box mt={2} mx={2} mb={3} display="flex" justifyContent="center">
                      <CircularProgress />
                    </Box>
                  ) : (
                    <ExperienceWalletSkillsAutocomplete
                      value={values.skills ?? []}
                      onChange={(_, newValue) => {
                        const maxSkillsAllowed = 100;
                        // If removing instead of adding
                        if (newValue.length < values.skills?.length) {
                          const newIds = newValue.map((sk) => sk.id);
                          // We find the deleted item
                          const deletedItem = values.skills?.reduce((acc, curr) => {
                            if (!newIds.includes(curr.id)) {
                              return curr;
                            }
                            return acc;
                          }, {});
                          // Preventing removing wallet items
                          if (deletedItem.wallet) {
                            return;
                          }
                        }
                        setFieldValue('skills', newValue.slice(0, maxSkillsAllowed));
                        if (newValue.length > maxSkillsAllowed) {
                          enqueueSnackbar(
                            intl.formatMessage({ id: 'experience.form.maxSkills' }, { n: maxSkillsAllowed }),
                            {
                              variant: 'error',
                            }
                          );
                        }
                        if (newValue.length > values.skills.length) {
                          trackEvent('EXPERIENCE_MANUAL_SKILLSADD');
                        } else {
                          trackEvent('EXPERIENCE_MANUAL_SKILLSREMOVE');
                        }
                      }}
                      helperText={touched.skills && errors.skills}
                      error={touched.skills && Boolean(errors.skills)}
                      isDisabled={gettingSkills}
                      open={skillsOpen}
                      setOpen={setSkillsOpen}
                      id="skills-add-experience-dropdown"
                    />
                  )}
                </Box>
                {(effectiveFormScope === 'work' || isMilitaryForm) && (
                  <Box sx={{ display: 'flex', mb: 1, flexDirection: 'column' }}>
                    <Typography sx={{ fontWeight: 700 }}>
                      {intl.formatMessage({
                        id: isMilitaryForm ? 'experience.form.militaryActivities' : 'experience.form.comments',
                      })}
                    </Typography>
                    <Box>
                      <TextField
                        error={touched.comments && Boolean(errors.comments)}
                        fullWidth
                        helperText={
                          touched.comments && errors.comments
                            ? errors.comments
                            : `${(values.comments || '').length}/500`
                        }
                        margin="normal"
                        name="comments"
                        disabled={isWalletEdit}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        type="text"
                        multiline
                        minRows={3}
                        variant="filled"
                        value={values.comments ?? ''}
                        placeholder={intl.formatMessage({ id: 'experience.form.commentsPlaceholder' })}
                        inputProps={{
                          maxLength: 500,
                          title: intl.formatMessage({ id: 'experience.form.comments' }),
                          style: { padding: 0 },
                          sx: {
                            '&::placeholder': {
                              opacity: 1,
                            },
                          },
                        }}
                        id="comments-add-experience-input"
                        sx={{
                          '.MuiInputBase-root': { paddingTop: '11px !important' },
                        }}
                      />
                    </Box>
                  </Box>
                )}
                {isMilitaryForm && (
                  <Box sx={{ display: 'flex', mb: 0, flexDirection: 'column' }}>
                    <RequiredLabel id="experience.form.militaryDates" />
                    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1} sx={{ mb: -2 }}>
                      {[
                        {
                          key: 'start_date',
                          labelId: 'experience.form.startDate',
                          id: 'start-date-military-dropdown',
                          show: true,
                        },
                        {
                          key: 'end_date',
                          labelId: 'experience.form.endDate',
                          id: 'end-date-military-dropdown',
                          show: !isCurrentJob,
                        },
                      ]
                        .filter(({ show }) => show)
                        .map(({ key, labelId, id: pickerId }) => (
                          <FormControl key={key} error={touched[key] && Boolean(errors[key])}>
                            <DatePicker
                              views={['month', 'year']}
                              format="MM/yyyy"
                              label={intl.formatMessage({ id: labelId })}
                              value={parseMMYYYY(values[key])}
                              onChange={(date) => setFieldValue(key, formatMMYYYY(date))}
                              disabled={isWalletEdit}
                              disableFuture
                              minDate={key === 'end_date' ? parseMMYYYY(values.start_date) : undefined}
                              slotProps={{
                                textField: {
                                  id: pickerId,
                                  name: key,
                                  variant: 'filled',
                                  fullWidth: true,
                                  error: touched[key] && Boolean(errors[key]),
                                  helperText: touched[key] && errors[key] ? errors[key] : undefined,
                                  onBlur: handleBlur,
                                  sx: { '.MuiInputBase-root': { backgroundColor: 'white' } },
                                },
                              }}
                            />
                          </FormControl>
                        ))}
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 2,
                        mt: 0,
                        position: 'relative',
                        left: 2,
                      }}
                    >
                      <Checkbox
                        sx={{ padding: '0 8px 0 8px' }}
                        color="primary"
                        checked={isCurrentJob}
                        onChange={(e) => {
                          setIsCurrentJob(e.currentTarget.checked);
                          setIsExperienceCurrent(false);
                          setFieldValue('end_date', null);
                        }}
                      />
                      <Typography sx={{ fontSize: 12, userSelect: 'none' }}>
                        {intl.formatMessage({ id: 'experience.form.currentlyEnlisted' })}
                      </Typography>
                    </Box>
                  </Box>
                )}
                {!isMilitaryForm && (
                  <Box sx={{ display: 'flex', mb: 0, flexDirection: 'column' }}>
                    <Typography sx={{ fontWeight: 700 }}>
                      {intl.formatMessage({ id: 'experience.form.workDuration' })}
                      <Typography sx={{ color: (theme) => theme.palette.error.main }} component="span">
                        *
                      </Typography>
                    </Typography>
                    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1} sx={{ mb: -2 }}>
                      {[
                        {
                          key: 'start_date',
                          labelId: 'experience.form.startDate',
                          id: 'start-date-experience-dropdown',
                          show: true,
                        },
                        {
                          key: 'end_date',
                          labelId: 'experience.form.endDate',
                          id: 'end-date-experience-dropdown',
                          show: !isCurrentJob && !isExperienceCurrent,
                        },
                      ]
                        .filter(({ show }) => show)
                        .map(({ key, labelId, id: pickerId }) => (
                          <FormControl
                            key={key}
                            error={touched[key] && Boolean(errors[key])}
                            sx={{ '.MuiInputBase-root': { backgroundColor: 'white' } }}
                          >
                            <DatePicker
                              views={['month', 'year']}
                              format="MM/yyyy"
                              label={intl.formatMessage({ id: labelId })}
                              value={parseMMYYYY(values[key])}
                              onChange={(date) => setFieldValue(key, formatMMYYYY(date))}
                              disabled={isWalletEdit}
                              disableFuture
                              minDate={key === 'end_date' ? parseMMYYYY(values.start_date) : undefined}
                              slotProps={{
                                textField: {
                                  id: pickerId,
                                  name: key,
                                  variant: 'filled',
                                  fullWidth: true,
                                  error: touched[key] && Boolean(errors[key]),
                                  helperText: touched[key] && errors[key] ? errors[key] : '',
                                  onBlur: handleBlur,
                                  sx: { '.MuiInputBase-root': { backgroundColor: 'white' } },
                                },
                              }}
                            />
                          </FormControl>
                        ))}
                    </Box>
                    {effectiveFormScope === 'work' ? (
                      <>
                        <FormControl sx={{ mb: 0, mt: 0, position: 'relative', top: -2, left: 2 }}>
                          <FormControlLabel
                            sx={{ '& .MuiFormControlLabel-label': { fontSize: 12, userSelect: 'none' } }}
                            label={intl.formatMessage({
                              id: isMilitaryForm
                                ? 'experience.form.currentlyEnlisted'
                                : 'onboarding.experience.currentEmployer',
                            })}
                            control={
                              <Checkbox
                                sx={{ padding: '0 8px 0 8px' }}
                                color="primary"
                                checked={isCurrentJob}
                                onChange={(e) => {
                                  setIsCurrentJob(e.currentTarget.checked);
                                  setIsExperienceCurrent(false);
                                  setFieldValue('end_date', null);
                                }}
                              />
                            }
                            labelPlacement="end"
                          />
                        </FormControl>
                        <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1 }}>
                          {intl.formatMessage({ id: 'onboarding.experience.oneDayHint' })}
                        </Typography>
                      </>
                    ) : effectiveFormScope === 'education' ? (
                      <>
                        <FormControl sx={{ mb: 0, mt: 0, position: 'relative', top: -2, left: 2 }}>
                          <FormControlLabel
                            sx={{ '& .MuiFormControlLabel-label': { fontSize: 12, userSelect: 'none' } }}
                            label={intl.formatMessage({ id: 'onboarding.experience.experienceCurrent' })}
                            control={
                              <Checkbox
                                sx={{ padding: '0 8px 0 8px' }}
                                color="primary"
                                checked={isExperienceCurrent}
                                onChange={(e) => {
                                  setIsExperienceCurrent(e.currentTarget.checked);
                                  setIsCurrentJob(false);
                                  setFieldValue('end_date', null);
                                }}
                              />
                            }
                            labelPlacement="end"
                          />
                        </FormControl>
                        <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1 }}>
                          {intl.formatMessage({ id: 'onboarding.experience.oneDayHint' })}
                        </Typography>
                      </>
                    ) : formScope === 'military' && values.experienceCategory === 'military' ? (
                      <FormControl sx={{ mb: 0, mt: 0, position: 'relative', top: -2, left: 2 }}>
                        <FormControlLabel
                          sx={{
                            '& .MuiFormControlLabel-label': {
                              fontSize: 12,
                            },
                          }}
                          label={intl.formatMessage({ id: 'onboarding.experience.present' })}
                          control={
                            <Checkbox
                              sx={{ padding: '0 8px 0 8px' }}
                              color="primary"
                              checked={isCurrentJob}
                              onChange={(e) => {
                                setIsCurrentJob(e.currentTarget.checked);
                                setIsExperienceCurrent(false);
                                setFieldValue('end_date', null);
                              }}
                            />
                          }
                          labelPlacement="end"
                        />
                      </FormControl>
                    ) : (
                      values.type !== '' && (
                        <FormControl sx={{ mb: 1, position: 'relative', top: -2, left: 2 }}>
                          <FormControlLabel
                            sx={{
                              '& .MuiFormControlLabel-label': {
                                fontSize: 12,
                              },
                            }}
                            label={intl.formatMessage({ id: 'onboarding.experience.experienceCurrent' })}
                            control={
                              <Checkbox
                                sx={{ padding: '0 8px 0 8px' }}
                                color="primary"
                                checked={isExperienceCurrent}
                                onChange={(e) => {
                                  setIsExperienceCurrent(e.currentTarget.checked);
                                  setIsCurrentJob(false);
                                  setFieldValue('end_date', null);
                                }}
                              />
                            }
                            labelPlacement="end"
                          />
                        </FormControl>
                      )
                    )}
                  </Box>
                )}
                {setCurrentContent && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                    <Button
                      aria-label="cancel"
                      variant="outlined"
                      color="inherit"
                      sx={{ opacity: 0.6, mr: 2 }}
                      onClick={() => setCurrentContent('display')}
                      type="button"
                      disabled={isSubmitting || gettingSkills}
                      id="cancel-experience-dropdown"
                    >
                      {intl.formatMessage({ id: 'jobSearchActivity.form.cancel' })}
                    </Button>
                    {
                      <Button
                        aria-label="done"
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={isSubmitting || gettingSkills}
                        id={appendItem ? 'add-experience-dropdown' : 'done-experience-button'}
                        startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
                      >
                        {isSubmitting
                          ? intl.formatMessage({ id: 'experience.form.saving' })
                          : intl.formatMessage({ id: appendItem ? 'experience.add' : 'experience.form.done' })}
                      </Button>
                    }
                  </Box>
                )}
              </>
            )}
          </form>
        );
      }}
    </Formik>
  );
}

ExperienceWalletForm.propTypes = {
  currentIndex: PropTypes.number,
  setCurrentContent: PropTypes.func,
  replaceItem: PropTypes.func,
  appendItem: PropTypes.func,
  experience: PropTypes.shape({
    id: PropTypes.string,
    field: PropTypes.object,
    title: PropTypes.string,
    type: PropTypes.string,
    level: PropTypes.string,
    start_date: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    end_date: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    employer_provider: PropTypes.string,
    skills: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        slice: PropTypes.func,
      })
    ),
  }),
  experiences: PropTypes.array,
  onSubmit: PropTypes.func,
  formScope: PropTypes.oneOf(['education', 'work', 'military']).isRequired,
  formId: PropTypes.string,
  onCategoryChange: PropTypes.func,
  onSubmittingChange: PropTypes.func,
  hasSelectedCategory: PropTypes.bool,
  isResumeBuilder: PropTypes.bool,
  showMilitaryCourse: PropTypes.bool,
  saveMilitaryCourses: PropTypes.func,
  saveAcademicDegrees: PropTypes.func,
};
